import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // Wait for the server before removing the row from the DOM. updateQuantity()
    // disables the cart for the duration of the request and, on success, morphs
    // the re-rendered section — which is what actually removes the row (or swaps
    // in the empty state). Removing it optimistically here made the row vanish
    // before the request settled and could desync the drawer if it failed.
    // Mirrors Mimco's live drawer, which keeps the row until the request returns.
    // A loading class swaps the row's remove icon for a spinner meanwhile, so
    // the click isn't left without feedback during the request.
    const row = this.refs.cartItemRows[line - 1];

    // Was the removal triggered from the keyboard? The activated Remove button is the
    // focused element right now, and `:focus-visible` matches only for keyboard focus.
    // We restore focus (below) only in that case: a mouse user doesn't need it and
    // would otherwise get an unexpected focus ring on the neighbouring button, whereas
    // a keyboard/screen-reader user must not be dropped to <body> (WCAG 2.4.3).
    const activeOnRemove = document.activeElement;
    const keyboardInitiated = activeOnRemove instanceof HTMLElement && activeOnRemove.matches(':focus-visible');

    row?.classList.add('cart-items__table-row--removing');

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    })
      ?.then(() => {
        // The morph inside updateQuantity deletes the removed row — including the
        // Remove button that had focus — which would otherwise drop focus to <body>.
        if (keyboardInitiated) this.#restoreFocusAfterRemove(line);
      })
      .catch(() => {
        // Request failed: drop the spinner so the row returns to its normal state.
        // The error itself is surfaced by updateQuantity's own error handler.
        row?.classList.remove('cart-items__table-row--removing');
      });
  }

  /**
   * Moves keyboard focus to a sensible neighbour after a line item's row has been
   * morphed out of the DOM, so focus isn't dropped to `<body>`. Deferred a frame so
   * the section morph has settled. Whether a focus ring actually shows is left to
   * `:focus-visible`, so mouse-initiated removals stay ring-free while keyboard ones
   * keep a visible focus. Defensive: never throws into the removal promise chain.
   *
   * @param {number} removedLine - The 1-based line number that was removed.
   */
  #restoreFocusAfterRemove(removedLine) {
    requestAnimationFrame(() => {
      const removeButtons = /** @type {HTMLElement[]} */ (
        Array.from(this.querySelectorAll('.cart-items__remove:not([hidden])'))
      );

      if (removeButtons.length > 0) {
        // Buttons are in line order; the removed slot is now filled by the next
        // item (same index), except when the last item was removed — clamp then.
        const index = Math.min(removedLine - 1, removeButtons.length - 1);
        removeButtons[index]?.focus();
        return;
      }

      // Cart is now empty. Focus a stable landmark rather than letting focus fall to
      // <body>: the drawer's close button, or the cart page's empty-state CTA.
      const drawerClose = /** @type {HTMLElement | null} */ (
        this.closest('theme-drawer')?.querySelector('.theme-drawer__close-button')
      );
      const emptyButton = /** @type {HTMLElement | null} */ (this.querySelector('.cart-items__empty-button'));
      const fallback = this.isDrawer ? (drawerClose ?? emptyButton) : (emptyButton ?? drawerClose);
      fallback?.focus();
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // Shopify's cart/change.js responds with a numeric `status` (e.g. 422) and a
        // `message` field, not `errors`, when the requested quantity exceeds available
        // inventory. It also clamps the line's quantity server-side despite erroring, so
        // `sections` is absent here — resync the section below rather than trusting the
        // stale local DOM.
        if (parsedResponseText.errors || parsedResponseText.status) {
          const errorMessage = parsedResponseText.errors || parsedResponseText.message || 'Cart update failed';

          // Resync BEFORE showing the error: the section morph replaces this row's markup
          // wholesale, so showing the error first would have it wiped out immediately by
          // the morph that follows.
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          this.#handleCartError(line, parsedResponseText);

          // Resolve (don't reject) with the real post-clamp cart: Shopify may have
          // partially applied the change server-side despite erroring, so other listeners
          // (e.g. the header cart-icon bubble, header-actions' live region) need the
          // actual item count here rather than being starved of it by a rejection. Mirrors
          // product-form.js's add-to-cart error handling, which resolves with a refreshed
          // cart the same way.
          try {
            const ajaxCart = await this.fetchCartData();
            deferredUpdatePromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                items: ajaxCart.items,
                itemCount: ajaxCart.item_count,
                source: 'cart-items-component',
                didError: true,
              },
            });
          } catch (fetchError) {
            deferredUpdatePromise.reject(fetchError instanceof Error ? fetchError : new Error(errorMessage));
          }

          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // Shopify keeps discount codes on the cart until explicitly cleared, so an
        // emptied cart would otherwise silently carry a previously-applied code onto
        // whatever gets added next. Clear it before resolving: onLineItemRemove restores
        // focus off this promise's resolution, and clearing after resolving would let that
        // focus move race a second, later morph of this section (WCAG 2.4.3).
        const shouldClearDiscounts = newCartItemCount === 0 && parsedResponseText.discount_codes?.length > 0;
        const sectionHtml = shouldClearDiscounts
          ? ((await this.#clearDiscountCodes()) ?? parsedResponseText.sections[this.sectionId])
          : parsedResponseText.sections[this.sectionId];

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        // INTENTIONAL: this morph is NOT wrapped in startViewTransition, even when a
        // removal empties the drawer. Doing so (to animate the empty-state swap, matching
        // the empty → non-empty fill transition in #handleCartUpdate) would defer the DOM
        // update into the transition callback, which runs *after* the focus-restoration
        // rAF in onLineItemRemove — the rAF would then focus the row that's about to be
        // removed and drop focus to <body> when it vanishes, reintroducing the keyboard
        // focus-loss bug (WCAG 2.4.3). If the animation is wanted, couple the focus move
        // to the transition's completion instead of restoring it separately.
        morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });

    return deferredUpdatePromise.promise;
  }

  /**
   * Clears any previously-applied discount codes once the cart is emptied. Shopify
   * keeps discount_codes on the cart until explicitly cleared, so without this an
   * emptied cart would silently carry a previously-applied code onto whatever gets
   * added next. Mirrors cart-discount.js's apply/remove flow: only this component's
   * own section is fetched here — other cart-items-component instances (e.g. the
   * drawer vs. the page) resync via the CartDiscountUpdateEvent dispatched below,
   * same as a manual discount removal would.
   * @returns {Promise<string | undefined>} The refreshed section HTML for `this.sectionId`,
   *   or undefined if the request failed.
   */
  async #clearDiscountCodes() {
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: [],
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', { body: JSON.stringify({ discount: '', sections: [this.sectionId] }) })
      );
      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      return data.sections?.[this.sectionId];
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to clear discount codes',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
      return undefined;
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} [parsedResponseText.errors] - The errors.
   * @param {string} [parsedResponseText.message] - The error message (e.g. quantity exceeding
   *   available inventory returns this instead of `errors`).
   */
  #handleCartError = (line, parsedResponseText) => {
    // Looked up via the row rather than `this.refs.quantitySelectors`: that ref only
    // exists where the quantity-selector-component custom element is rendered (e.g. the
    // drawer). Some brand overrides render the cart page's quantity control as a plain
    // `<select>` instead, so `quantitySelectors` is undefined there.
    const row = this.refs.cartItemRows[line - 1];
    // Scoped to `.cart-items__qty-select` rather than a bare `select[data-line]`: some
    // brand overrides render a separate variant/option `<select>` that also carries
    // `data-line` earlier in the row, which a bare attribute selector would match instead.
    const quantityControl = row?.querySelector('input[data-cart-line], select.cart-items__qty-select[data-line]');

    if (quantityControl instanceof HTMLInputElement) {
      quantityControl.value = quantityControl.defaultValue;
    } else if (quantityControl instanceof HTMLSelectElement) {
      const defaultOption = quantityControl.querySelector('option[selected]');
      if (defaultOption instanceof HTMLOptionElement) quantityControl.value = defaultOption.value;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    const errorMessage = parsedResponseText.errors || parsedResponseText.message || 'Cart update failed';

    // Degrade rather than throw if the markup is missing: this runs mid cart-error-handling
    // (called from `updateQuantity()`, which still needs to resolve with the refreshed cart
    // afterward), so throwing here would propagate up and replace `errorMessage` with an
    // internal diagnostic string in the CartErrorEvent dispatched below, and skip that
    // refresh entirely.
    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.error(`Cart item error elements not found for line ${line}`);
    } else {
      // Reveal the live region BEFORE writing its text. The container is `display: none`
      // while hidden, so it isn't in the accessibility tree; mutating its text first and
      // unhiding after would not reliably trigger the `role="alert"` announcement.
      cartItemErrorContainer.classList.remove('hidden');
      cartItemError.textContent = errorMessage;
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items while a cart update is in flight. `aria-busy` tells
   * assistive tech the region is updating, so it doesn't announce the transient
   * mid-update DOM; `cart-items-disabled` blocks pointer interaction visually.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Re-enables the cart items once the update has settled.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
